import React, { useEffect, useRef, useState } from 'react';
import { FormElement, FormInput, FormNumberInput, DragHandle, FormLabel, ResizeIcon, ExpandCollapseButton } from "../components/StyledElements";
import { Handle, Position, NodeResizeControl, useUpdateNodeInternals } from '@xyflow/react';
import { NodeCard } from './NodeCard';
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { monokai } from '@uiw/codemirror-theme-monokai';

import { EditorView } from "@codemirror/view";
import { CloseButton } from '../components/CloseButton';
import { inputsNeedRepair, normalizeNodeInputs } from '../utils/nodeInputs';

export const PythonNodeCard = ({ onAdd = () => { }, style = {} }) => {
  return (
    <NodeCard title="Python" description="Transform data using a python code" onAdd={() => {
      console.log("!!! ON ADD PYTHON NODE");
      onAdd(500);
    }}
      style={style} />
  )
};

export const PythonNode = ({ id, data }) => {
  const updateNodeInternals = useUpdateNodeInternals();
  // A Python node always has at least the implicit `input`, so a graph that says otherwise is
  // repaired once and then left to the user.
  const seededInputs = useRef(false);
  console.log("!!! Python NODE DATA: ", data);
  const [localData, setLocalData] = useState({
    code: "",
    inputCount: 1,
    inputs: [{ id: "input-1", label: "input" }],
  });
  const topSectionRef = useRef(null);


  useEffect(() => {
    if (topSectionRef.current) {
      const height = topSectionRef.current.getBoundingClientRect().height;
      console.log("!!! TOP SECTION HEIGHT: ", height);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localData.inputCount, topSectionRef.current]);

  useEffect(() => {
    console.log("!!!MODEL: ", data.model);
    if (!data) {
      return;
    }
    // `inputCount` and `inputs` can disagree in generated graphs (e.g. 0 with []), which
    // used to render more rows than there were inputs. A Python node always exposes at
    // least `input`, so the floor is one.
    const normalized = normalizeNodeInputs(data, { minCount: 1 });

    setLocalData({
      code: data.code ?? "",
      ...normalized,
    });

    if (!seededInputs.current) {
      seededInputs.current = true;
      if (inputsNeedRepair(data, normalized)) {
        // Persist the correction so the saved graph matches what is shown.
        data.onUpdateData?.(id, normalized);
      }
      // Re-measure once the list has settled, so React Flow knows the new handle(s).
      updateNodeInternals(id);
    }
    // `id` is stable for the life of the node, and updateNodeInternals is memoised by
    // React Flow; listing both keeps the lint rule honest.
  }, [data, id, updateNodeInternals]);


  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "10px", border: "1px solid var(--main-border-color)", borderRadius: "8px", backgroundColor: "var(--content-item-bg)", height: "100%", width: "100%", boxSizing: "border-box", position: "relative" }}>
      <NodeResizeControl style={{ zIndex: 40000 }}>
        <ResizeIcon />
      </NodeResizeControl>
      <ExpandCollapseButton onClick={() => {
      }} />
      <CloseButton onClick={() => {
        data.onDelete(id);
      }} />
      {/* flex: 1 + minHeight: 0 so this column fills the card */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", flex: 1, minHeight: 0 }}>
        <div ref={topSectionRef}>
          <DragHandle />
          <FormLabel label="Python Code" />
          <FormElement>
            <FormLabel label="Input Count" />
            <FormNumberInput
              value={localData.inputCount}
              onChange={(e) => {
                // Commit once after resizing; shrinking previously never persisted because
                // the update sat inside the grow loop.
                const desired = Math.max(1, Number(e.target.value) || 0);
                const next = normalizeNodeInputs(
                  { inputs: localData.inputs.slice(0, desired), inputCount: desired },
                  { truncate: true, minCount: 1 }
                );
                setLocalData({ ...localData, ...next });
                data.onUpdateData(id, next);
                updateNodeInternals(id);
              }}
              min={1}
              step={1}
            />
          </FormElement>
          {
            Array.from({ length: localData.inputCount }).map((_, index) => {
              const input = localData.inputs[index];

              return (
                // The handle lives inside its row: a fixed offset from the node's top drifted
                // into the code editor as rows were added, making it hard to find and drop on.
                <div key={input?.id ?? index} style={{ position: "relative" }}>
                  <FormElement>
                    <FormInput
                      placeholder={`Enter label for input ${index + 1}`}
                      value={input?.label ?? ""}
                      onChange={(e) => {
                        const newLabel = e.target.value;
                        const oldInput = localData.inputs[index] ?? { id: `input-${index + 1}` };
                        const newInputs = [...localData.inputs];
                        newInputs[index] = { ...oldInput, label: newLabel };
                        setLocalData({ ...localData, inputs: newInputs });
                        data.onUpdateData(id, { inputs: newInputs });
                        data.onRenameInput?.(id, oldInput.id, newLabel);
                        console.log("!!! INPUT LABEL CHANGE: ", newInputs);
                        updateNodeInternals(id);
                      }}
                    />
                  </FormElement>
                  {input && (
                    <Handle
                      type="target"
                      position={Position.Left}
                      id={input.id}
                      style={{ top: "50%", left: "-16px", height: "12px", width: "12px", zIndex: 10 }}
                    />
                  )}
                </div>
              );
            })
          }
        </div>
        {/* Same trick: grow to fill parent, allow shrinking */}
        <FormElement style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px", flex: 1, minHeight: 0 }}>
          <div style={{ width: "100%", fontSize: "14px", color: "var(--main-font-color)" }} title="
            Enter Python code to transform the input data. The input data will be passed as a variable named 'input', or any of the labelled inputs defined above. You can have a space in the label, but it will be accessed with an underscore as a python variable. For example, 'input 2' will be accessible as input_2 in python. The output of the code should be stored in a variable named 'output'.">
            Info
            </div>
          <CodeMirror
            extensions={[python(), EditorView.lineWrapping]}
            label="Code"
            theme={monokai}
            name="code"
            value={localData?.code ?? ""}
            onChange={(code) => {
              setLocalData({ ...localData, code: code });
              data.onUpdateData(id, { code: code });
            }}
            height="100%"
            width="100%"
            minHeight="200px"
            className="nodrag nowheel"
            style={{
              fontSize: "16px",
              border: "1px solid var(--main-border-color)",
              borderRadius: "4px",
              flex: 1,
              overflow: "hidden",
              minHeight: "200px",
            }}

          />
        </FormElement>
      </div>
      <Handle type="source" position={Position.Right} style={{ height: "12px", width: "12px" }} id="output" />
    </div>
  );

};

export default PythonNode;
